/* ==========================================================================
   DF Creación — JS de marca (vainilla, sin librerías)
   Una función init por componente, todas llamadas desde un único
   DOMContentLoaded. Cada init comprueba que su elemento existe.
   ========================================================================== */

(function () {
  'use strict';

  /* Varias secciones incluyen este archivo por su cuenta (para que cada
     una funcione sola si Shopify la renderiza suelta, p. ej. en el editor
     de temas), además de la carga global en layout/theme.liquid. Eso hace
     que en páginas con más de una de esas secciones el <script> aparezca
     repetido y este archivo se ejecute más de una vez. Sin este freno,
     cada ejecución de más volvía a registrar los mismos listeners y — más
     grave — volvía a crear por duplicado los campos ocultos que adjuntan
     el diseño del cliente al pedido (properties[...]), duplicando la
     imagen y el texto en cada pedido real. */
  if (window.__dfcScriptsInit) return;
  window.__dfcScriptsInit = true;

  /* ---------- Reveal al hacer scroll (compartido para toda la web) ---------- */
  function initReveals() {
    var els = document.querySelectorAll('.dfc-reveal');
    if (!els.length) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(function (el) {
        el.classList.add('dfc-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var delay = entry.target.getAttribute('data-dfc-delay') || 0;
            setTimeout(function () {
              entry.target.classList.add('dfc-visible');
            }, Number(delay));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );

    els.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---------- Quitar fondo (automático, 100% en el navegador) ----------
     Compartido entre las 3 zonas del previsualizador (delantera/trasera/
     manga) para no cargar la IA más de una vez.
     Dos métodos:
     1) IA (@imgly/background-removal, cargada solo cuando hace falta):
        recorta con calidad real, sirve para fotos con fondos complejos.
        Se descarga (unos MB) la primera vez que el cliente activa la
        casilla; nunca se sube la imagen a ningún servidor, el modelo
        corre entero en su navegador.
     2) Reserva por color: si la IA no carga (sin conexión, navegador
        antiguo...), recorre el borde de la imagen y hace transparente la
        zona conectada a ese borde cuyo color se parezca al fondo. */
  var dfcIaCargada = null; // promesa cacheada, para no recargar la IA cada vez

  function cargarIaQuitarFondo() {
    if (!dfcIaCargada) {
      dfcIaCargada = import('https://esm.sh/@imgly/background-removal@1.5.7');
    }
    return dfcIaCargada;
  }

  function quitarFondoIA(imgUrl, callback) {
    cargarIaQuitarFondo()
      .then(function (mod) {
        return mod.removeBackground(imgUrl);
      })
      .then(function (blob) {
        callback(URL.createObjectURL(blob));
      })
      .catch(function () {
        // Si la IA falla (sin conexión, navegador no compatible...),
        // usamos el método de color como reserva.
        quitarFondoColor(imgUrl, callback);
      });
  }

  function quitarFondoColor(imgUrl, callback) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        var maxDim = 1400;
        var scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
        var w = Math.max(1, Math.round(img.naturalWidth * scale));
        var h = Math.max(1, Math.round(img.naturalHeight * scale));

        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        var ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        var imageData = ctx.getImageData(0, 0, w, h);
        var data = imageData.data;

        function idx(x, y) {
          return (y * w + x) * 4;
        }

        // Color de fondo: promedio de las 4 esquinas.
        var corners = [
          [0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]
        ];
        var br = 0, bg = 0, bb = 0;
        corners.forEach(function (c) {
          var i = idx(c[0], c[1]);
          br += data[i]; bg += data[i + 1]; bb += data[i + 2];
        });
        br /= 4; bg /= 4; bb /= 4;

        var tolerance = 42;
        var visited = new Uint8Array(w * h);
        var queue = [];

        function pushIfEdgeUnvisited(x, y) {
          var p = y * w + x;
          if (!visited[p]) queue.push(x, y);
        }

        for (var x = 0; x < w; x++) {
          pushIfEdgeUnvisited(x, 0);
          pushIfEdgeUnvisited(x, h - 1);
        }
        for (var y = 0; y < h; y++) {
          pushIfEdgeUnvisited(0, y);
          pushIfEdgeUnvisited(w - 1, y);
        }

        while (queue.length) {
          var qy = queue.pop();
          var qx = queue.pop();
          if (qx < 0 || qy < 0 || qx >= w || qy >= h) continue;
          var p = qy * w + qx;
          if (visited[p]) continue;
          visited[p] = 1;

          var i = idx(qx, qy);
          var dr = data[i] - br;
          var dg = data[i + 1] - bg;
          var db = data[i + 2] - bb;
          var dist = Math.sqrt(dr * dr + dg * dg + db * db);

          if (dist <= tolerance) {
            data[i + 3] = 0;
            queue.push(qx + 1, qy, qx - 1, qy, qx, qy + 1, qx, qy - 1);
          }
        }

        ctx.putImageData(imageData, 0, 0);
        callback(canvas.toDataURL('image/png'));
      } catch (err) {
        // Si la imagen viene de otro dominio y el navegador bloquea leer
        // sus píxeles (CORS), no rompemos nada: seguimos con la original.
        callback(null);
      }
    };
    img.onerror = function () {
      callback(null);
    };
    img.src = imgUrl;
  }

  /* ---------- Inclinación 3D tipo "tarjeta" sobre la prenda ----------
     Se aplica a la capa .dfc-pv-tilt (mockup + zona + diseño juntos, para
     que giren de forma coherente). Reacciona al puntero/dedo sobre el
     escenario, pero se desactiva mientras el cliente está arrastrando su
     diseño (para no pelearse con ese gesto) y respeta
     prefers-reduced-motion. */
  function setupTilt(stage, tiltEl, activo, maxAngle, estaArrastrando) {
    if (!stage || !tiltEl || !activo) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var angle = parseFloat(maxAngle);
    if (!angle || angle <= 0) angle = 12;

    function aplicar(clientX, clientY) {
      if (estaArrastrando()) return;
      var rect = stage.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      var relX = (clientX - rect.left) / rect.width;
      var relY = (clientY - rect.top) / rect.height;
      relX = Math.max(0, Math.min(1, relX));
      relY = Math.max(0, Math.min(1, relY));
      var rotY = (relX - 0.5) * 2 * angle;
      var rotX = -(relY - 0.5) * 2 * angle;
      tiltEl.classList.add('dfc-pv-tilt--instant');
      tiltEl.style.transform = 'rotateX(' + rotX.toFixed(2) + 'deg) rotateY(' + rotY.toFixed(2) + 'deg)';
    }

    function reiniciar() {
      tiltEl.classList.remove('dfc-pv-tilt--instant');
      tiltEl.style.transform = 'rotateX(0deg) rotateY(0deg)';
    }

    stage.addEventListener('mousemove', function (e) {
      if (estaArrastrando()) return;
      aplicar(e.clientX, e.clientY);
    });
    stage.addEventListener('mouseleave', reiniciar);

    stage.addEventListener('touchmove', function (e) {
      if (estaArrastrando()) return;
      var t = e.touches && e.touches[0];
      if (!t) return;
      aplicar(t.clientX, t.clientY);
    }, { passive: true });
    stage.addEventListener('touchend', reiniciar);
    stage.addEventListener('touchcancel', reiniciar);
  }

  /* ---------- Previsualizador de logo sobre la prenda ----------
     Ahora con 3 zonas independientes (delantera / trasera / manga): cada
     una guarda su propio diseño, posición y escala. Cambiar de pestaña no
     borra lo que ya se subió en las otras. */
  function initPrevisualizador() {
    var root = document.querySelector('[data-dfc-previsualizador]');
    if (!root) return;

    var tiltActivo = root.getAttribute('data-dfc-pv-tilt-enabled') === 'true';
    var tiltMax = root.getAttribute('data-dfc-pv-tilt-max');

    /* ---------- Elegir técnica (obligatorio, compartido entre las 4 zonas) ----------
       Hasta que el cliente no elige DTF/Sublimación/Bordado/Fullprint, las
       etiquetas de "Sube tu logo" de las 4 pestañas quedan visualmente
       apagadas y no abren el selector de archivo al pincharlas. */
    var dfcTecnicaSeleccionada = null;
    var tecnicaBotones = root.querySelectorAll('[data-dfc-pv-tecnica]');
    var uploadLabels = root.querySelectorAll('[data-dfc-pv-upload-label]');
    var tecnicaAviso = root.querySelector('[data-dfc-pv-tecnica-aviso]');

    function actualizarBloqueoSubida() {
      uploadLabels.forEach(function (label) {
        label.classList.toggle('dfc-pv-upload--bloqueado', !dfcTecnicaSeleccionada);
      });
    }
    actualizarBloqueoSubida();

    tecnicaBotones.forEach(function (boton) {
      boton.addEventListener('click', function () {
        dfcTecnicaSeleccionada = boton.getAttribute('data-dfc-pv-tecnica');
        fijarPropiedadTecnica(dfcTecnicaSeleccionada);
        tecnicaBotones.forEach(function (b) {
          var activa = b === boton;
          b.classList.toggle('dfc-pv-tecnica--activa', activa);
          b.setAttribute('aria-checked', activa ? 'true' : 'false');
        });
        if (tecnicaAviso) tecnicaAviso.hidden = true;
        actualizarBloqueoSubida();
      });
    });

    uploadLabels.forEach(function (label) {
      label.addEventListener('click', function (e) {
        if (!dfcTecnicaSeleccionada) {
          e.preventDefault();
          if (tecnicaAviso) tecnicaAviso.hidden = false;
        }
      });
    });

    /* ---------- Adjuntar el diseño personalizado al pedido ----------
       Todo lo de arriba es solo una vista previa en el navegador del
       cliente: por sí sola nunca llega nada a la tienda. Para que la
       imagen que sube, la técnica elegida y el ajuste (tamaño/posición)
       lleguen solos a cada pedido, los vamos guardando como "propiedades
       de línea" del producto (función nativa de Shopify, admite
       archivos: no hace falta ninguna app) dentro del propio formulario
       de compra, cada vez que el cliente cambia algo — así, cuando le da
       a "Añadir al carrito", ya van adjuntos sin depender de nada
       asíncrono en el último momento. */
    var productForm = document.querySelector('form[action*="/cart/add"]');
    var zonaEtiquetas = {
      delantera: 'Delantera',
      trasera: 'Trasera',
      manga_derecha: 'Manga derecha',
      manga_izquierda: 'Manga izquierda'
    };
    var tecnicaEtiquetas = { dtf: 'DTF', sublimacion: 'Sublimación' };
    var dfcTecnicaPropInput = null;

    function fijarPropiedadTecnica(tecnicaKey) {
      if (!productForm) return;
      if (!dfcTecnicaPropInput) {
        dfcTecnicaPropInput = document.createElement('input');
        dfcTecnicaPropInput.type = 'hidden';
        dfcTecnicaPropInput.name = 'properties[Técnica]';
        dfcTecnicaPropInput.setAttribute('data-dfc-pv-propiedad', '');
        productForm.appendChild(dfcTecnicaPropInput);
      }
      dfcTecnicaPropInput.value = tecnicaEtiquetas[tecnicaKey] || tecnicaKey;
    }

    function fijarPropiedadArchivo(zonaKey, file, refs) {
      if (!productForm || !file) return;
      var etiqueta = zonaEtiquetas[zonaKey] || zonaKey;
      if (!refs.fileProp) {
        refs.fileProp = document.createElement('input');
        refs.fileProp.type = 'file';
        refs.fileProp.name = 'properties[Diseño – ' + etiqueta + ']';
        refs.fileProp.style.display = 'none';
        refs.fileProp.setAttribute('data-dfc-pv-propiedad', '');
        productForm.appendChild(refs.fileProp);
      }
      var dt = new DataTransfer();
      dt.items.add(file);
      refs.fileProp.files = dt.files;
    }

    function fijarPropiedadAjuste(zonaKey, scale, xPct, yPct, refs) {
      if (!productForm) return;
      var etiqueta = zonaEtiquetas[zonaKey] || zonaKey;
      if (!refs.ajusteProp) {
        refs.ajusteProp = document.createElement('input');
        refs.ajusteProp.type = 'hidden';
        refs.ajusteProp.name = 'properties[Ajuste – ' + etiqueta + ']';
        refs.ajusteProp.setAttribute('data-dfc-pv-propiedad', '');
        productForm.appendChild(refs.ajusteProp);
      }
      refs.ajusteProp.value = 'Tamaño ' + Math.round(scale * 100) + '%, posición X ' + Math.round(xPct) + '%, Y ' + Math.round(yPct) + '%';
    }

    /* ---------- Pestañas: delantera / trasera / manga ---------- */
    var tabs = root.querySelectorAll('[data-dfc-pv-tab]');
    var paneles = root.querySelectorAll('[data-dfc-pv-panel]');
    if (tabs.length && paneles.length) {
      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var zona = tab.getAttribute('data-dfc-pv-tab');

          tabs.forEach(function (t) {
            var activo = t === tab;
            t.classList.toggle('dfc-pv-tab--activo', activo);
            t.setAttribute('aria-selected', activo ? 'true' : 'false');
          });

          paneles.forEach(function (panel) {
            panel.hidden = panel.getAttribute('data-dfc-pv-panel') !== zona;
          });
        });
      });
    }

    /* ---------- Una zona (panel) del previsualizador ---------- */
    function initZonaPanel(panel) {
      var fileInput = panel.querySelector('[data-dfc-pv-file]');
      var uploadLabel = panel.querySelector('[data-dfc-pv-upload-text]');
      var stage = panel.querySelector('[data-dfc-pv-stage]');
      var tiltEl = panel.querySelector('[data-dfc-pv-tilt]');
      var zona = panel.querySelector('[data-dfc-pv-zona]');
      var logoWrap = panel.querySelector('[data-dfc-pv-logo-wrap]');
      var logoImg = panel.querySelector('[data-dfc-pv-logo-img]');
      var scaleInput = panel.querySelector('[data-dfc-pv-scale]');
      var resetBtn = panel.querySelector('[data-dfc-pv-reset]');
      var centerBtn = panel.querySelector('[data-dfc-pv-center]');
      var bgToggleWrap = panel.querySelector('[data-dfc-pv-bg-toggle-wrap]');
      var bgToggle = panel.querySelector('[data-dfc-pv-remove-bg]');
      var bgEstado = panel.querySelector('[data-dfc-pv-bg-estado]');
      var zonaKey = panel.getAttribute('data-dfc-pv-panel');
      var propRefs = {};

      if (!stage || !zona || !logoWrap || !logoImg) return;

      var originalImageUrl = null;

      function aplicarFondo() {
      if (!originalImageUrl) return;

      if (!bgToggle || !bgToggle.checked) {
        logoImg.src = originalImageUrl;
        if (bgEstado) bgEstado.hidden = true;
        if (fileInput && fileInput.files && fileInput.files[0]) {
          fijarPropiedadArchivo(zonaKey, fileInput.files[0], propRefs);
        }
        return;
      }

      if (bgEstado) {
        bgEstado.hidden = false;
        bgEstado.textContent = 'Quitando el fondo… la primera vez puede tardar unos segundos.';
      }

      quitarFondoIA(originalImageUrl, function (resultado) {
        if (resultado) {
          logoImg.src = resultado;
          if (bgEstado) {
            bgEstado.textContent = 'Fondo eliminado. Si no ha quedado bien, desmarca la casilla.';
          }
          fetch(resultado)
            .then(function (r) { return r.blob(); })
            .then(function (blob) {
              var file = new File([blob], 'diseno-' + zonaKey + '-sin-fondo.png', { type: blob.type || 'image/png' });
              fijarPropiedadArchivo(zonaKey, file, propRefs);
            })
            .catch(function () {
              /* Si falla la conversión, el pedido se queda con la imagen
                 original (ya adjunta al subir el archivo); nunca bloqueamos
                 la compra por esto. */
            });
        } else {
          logoImg.src = originalImageUrl;
          if (bgEstado) {
            bgEstado.textContent = 'No se ha podido quitar el fondo de esta imagen. Se muestra la original.';
          }
        }
      });
    }

    if (bgToggle) {
      bgToggle.addEventListener('change', aplicarFondo);
    }

    var zoneRect = {
      x: parseFloat(zona.style.getPropertyValue('--x')) || 30,
      y: parseFloat(zona.style.getPropertyValue('--y')) || 25,
      w: parseFloat(zona.style.getPropertyValue('--w')) || 40,
      h: parseFloat(zona.style.getPropertyValue('--h')) || 35
    };

    var state = {
      scale: 1,
      xPct: 0, // posición relativa dentro de la zona, -50 a 50 (centro = 0)
      yPct: 0,
      dragging: false,
      startX: 0,
      startY: 0,
      startXPct: 0,
      startYPct: 0,
      baseSizePct: Math.min(zoneRect.w, zoneRect.h) * 0.9
    };

    function applyTransform() {
      var size = state.baseSizePct * state.scale;
      var centerX = zoneRect.x + zoneRect.w / 2 + state.xPct;
      var centerY = zoneRect.y + zoneRect.h / 2 + state.yPct;
      logoWrap.style.width = size + '%';
      logoWrap.style.height = size + '%';
      logoWrap.style.left = centerX + '%';
      logoWrap.style.top = centerY + '%';
      logoWrap.style.transform = 'translate(-50%, -50%)';
    }

    function resetState() {
      state.scale = 1;
      state.xPct = 0;
      state.yPct = 0;
      if (scaleInput) scaleInput.value = 1;
      applyTransform();
      if (fileInput && fileInput.files && fileInput.files[0]) {
        fijarPropiedadAjuste(zonaKey, state.scale, state.xPct, state.yPct, propRefs);
      }
    }

    zona.style.left = zoneRect.x + '%';
    zona.style.top = zoneRect.y + '%';
    zona.style.width = zoneRect.w + '%';
    zona.style.height = zoneRect.h + '%';

    if (fileInput) {
      fileInput.addEventListener('change', function (e) {
        var file = e.target.files && e.target.files[0];
        if (!file) return;
        var url = URL.createObjectURL(file);
        originalImageUrl = url;
        logoImg.src = url;
        logoWrap.classList.add('is-active');
        zona.style.display = 'none';
        if (uploadLabel) {
          uploadLabel.textContent = file.name;
        }
        if (bgToggleWrap) bgToggleWrap.hidden = false;
        if (bgToggle) bgToggle.checked = false;
        if (bgEstado) bgEstado.hidden = true;
        resetState();
        fijarPropiedadArchivo(zonaKey, file, propRefs);
      });
    }

    function clampPct() {
      var maxOffsetX = 50;
      var maxOffsetY = 50;
      state.xPct = Math.max(-maxOffsetX, Math.min(maxOffsetX, state.xPct));
      state.yPct = Math.max(-maxOffsetY, Math.min(maxOffsetY, state.yPct));
    }

    function pointerDown(e) {
      state.dragging = true;
      var point = e.touches ? e.touches[0] : e;
      state.startX = point.clientX;
      state.startY = point.clientY;
      state.startXPct = state.xPct;
      state.startYPct = state.yPct;
    }

    function pointerMove(e) {
      if (!state.dragging) return;
      var point = e.touches ? e.touches[0] : e;
      var rect = stage.getBoundingClientRect();
      var dxPct = ((point.clientX - state.startX) / rect.width) * 100;
      var dyPct = ((point.clientY - state.startY) / rect.height) * 100;
      state.xPct = state.startXPct + dxPct;
      state.yPct = state.startYPct + dyPct;
      clampPct();
      applyTransform();
      e.preventDefault();
    }

    function pointerUp() {
      if (state.dragging && fileInput && fileInput.files && fileInput.files[0]) {
        fijarPropiedadAjuste(zonaKey, state.scale, state.xPct, state.yPct, propRefs);
      }
      state.dragging = false;
    }

    logoWrap.addEventListener('mousedown', pointerDown);
    window.addEventListener('mousemove', pointerMove);
    window.addEventListener('mouseup', pointerUp);
    logoWrap.addEventListener('touchstart', pointerDown, { passive: true });
    window.addEventListener('touchmove', pointerMove, { passive: false });
    window.addEventListener('touchend', pointerUp);

    if (scaleInput) {
      scaleInput.addEventListener('input', function () {
        state.scale = parseFloat(scaleInput.value);
        applyTransform();
        if (fileInput && fileInput.files && fileInput.files[0]) {
          fijarPropiedadAjuste(zonaKey, state.scale, state.xPct, state.yPct, propRefs);
        }
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', resetState);
    }

    if (centerBtn) {
      centerBtn.addEventListener('click', function () {
        state.xPct = 0;
        state.yPct = 0;
        applyTransform();
        if (fileInput && fileInput.files && fileInput.files[0]) {
          fijarPropiedadAjuste(zonaKey, state.scale, state.xPct, state.yPct, propRefs);
        }
      });
    }

    applyTransform();

    /* Inclinación 3D de esta zona: gira el mockup + el diseño juntos,
       salvo mientras el cliente está arrastrando el diseño. */
    setupTilt(stage, tiltEl, tiltActivo, tiltMax, function () {
      return state.dragging;
    });

    }

    paneles.forEach(function (panel) {
      initZonaPanel(panel);
    });

    /* Actualiza el mockup al cambiar de color/variante, en TODAS las zonas
       que estén marcadas como "dinámicas" (data-dfc-pv-mockup-dinamico) —
       es decir, las que todavía no tienen su propia foto real fija subida
       por metacampo y están usando la foto delantera de respaldo. Antes
       esto solo se aplicaba a la zona "delantera", así que al entrar en
       "Trasera"/"Manga" después de cambiar de color se veía la foto del
       color original en vez del elegido — con esto las 4 zonas dinámicas
       se mantienen todas en el mismo color a la vez.

       Horizon (2024+) no expone un evento simple con la nueva variante: el
       cambio se resuelve por dentro de sus propios componentes. Para no
       depender de esos detalles internos (que pueden cambiar entre
       versiones del tema), vigilamos directamente el campo oculto
       `name="id"` del formulario de compra — es el dato canónico de "qué
       variante está seleccionada ahora mismo" — y cuando cambia, pedimos
       los datos de esa variante al propio Shopify. */
    var mockupsDinamicos = Array.prototype.filter.call(
      root.querySelectorAll('[data-dfc-pv-mockup]'),
      function (img) { return img.getAttribute('data-dfc-pv-mockup-dinamico') === 'true'; }
    );

    if (mockupsDinamicos.length && root.getAttribute('data-dfc-pv-product-url')) {
      var productUrl = root.getAttribute('data-dfc-pv-product-url');
      var variantIdInput = document.querySelector('input[name="id"]');

      if (variantIdInput) {
        var productJsonPromise = null;
        var lastVariantId = variantIdInput.value;

        function getProductJson() {
          if (!productJsonPromise) {
            productJsonPromise = fetch(productUrl + '.js').then(function (r) {
              return r.json();
            });
          }
          return productJsonPromise;
        }

        function actualizarMockupsPorVariante(variantId) {
          getProductJson()
            .then(function (productJson) {
              var variant = (productJson.variants || []).find(function (v) {
                return String(v.id) === String(variantId);
              });
              if (variant && variant.featured_image && variant.featured_image.src) {
                var src = variant.featured_image.src;
                var sep = src.indexOf('?') === -1 ? '?' : '&';
                var nuevoSrc = src + sep + 'width=1200';
                mockupsDinamicos.forEach(function (img) {
                  img.src = nuevoSrc;
                });
              }
            })
            .catch(function () {
              /* Si falla, dejamos los mockups como estaban: nunca rompemos la página por esto. */
            });
        }

        setInterval(function () {
          var current = variantIdInput.value;
          if (current && current !== lastVariantId) {
            lastVariantId = current;
            actualizarMockupsPorVariante(current);
          }
        }, 400);
      }
    }
  }

  /* ---------- Catálogo: selector guiado en 4 pasos ----------
     Género → tipo de prenda → material → productos. Los 13 productos
     categorizados (bloques "Producto categorizado" del editor) llegan ya
     como JSON embebido por Liquid en <script data-dfc-catalogo-datos>, así
     que todo el filtrado ocurre en el navegador, sin recargar la página.

     Degradación sin JavaScript: la sección siempre renderiza también una
     cuadrícula estática con TODOS los productos (data-dfc-catalogo-fallback),
     visible por defecto. Este script solo la oculta y muestra el asistente
     si consigue leer y entender los datos; si algo falla (JSON roto,
     navegador antiguo...) no tocamos nada y esa cuadrícula se queda tal
     cual — nunca una sección vacía o rota. */
  function initCatalogoWizard() {
    var secciones = document.querySelectorAll('.dfc-catalogo');

    secciones.forEach(function (seccion) {
      var wizard = seccion.querySelector('[data-dfc-wizard]');
      var fallback = seccion.querySelector('[data-dfc-catalogo-fallback]');
      var datosEl = seccion.querySelector('[data-dfc-catalogo-datos]');
      var configEl = seccion.querySelector('[data-dfc-catalogo-config]');
      if (!wizard || !datosEl || !configEl) return;

      var items, cfg;
      try {
        items = JSON.parse(datosEl.textContent);
        cfg = JSON.parse(configEl.textContent);
      } catch (err) {
        return; // JSON roto: dejamos la cuadrícula de reserva tal cual.
      }
      if (!items || !items.length || !cfg) return;

      // A partir de aquí tenemos datos válidos: activamos el asistente.
      if (fallback) fallback.hidden = true;
      wizard.hidden = false;

      var TIPOS = ['camiseta', 'sudadera', 'otros', 'accesorio'];
      var MATERIALES = ['tecnico', 'normal'];

      function itemsPorGenero(genero) {
        return items.filter(function (i) {
          return i.generos && i.generos.indexOf(genero) !== -1;
        });
      }

      function tiposDisponibles(genero) {
        var pool = itemsPorGenero(genero);
        return TIPOS.filter(function (t) {
          return pool.some(function (i) { return i.tipo === t; });
        });
      }

      function materialesDisponibles(genero, tipo) {
        var pool = itemsPorGenero(genero).filter(function (i) { return i.tipo === tipo; });
        return MATERIALES.filter(function (m) {
          return pool.some(function (i) { return i.material === m; });
        });
      }

      function itemsFinal(genero, tipo, material) {
        return itemsPorGenero(genero).filter(function (i) {
          return i.tipo === tipo && i.material === material;
        });
      }

      function limpiar() {
        wizard.innerHTML = '';
      }

      function crumbBtn(texto, onClick) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'dfc-wizard-crumb';
        b.textContent = texto;
        if (onClick) {
          b.addEventListener('click', onClick);
        } else {
          b.disabled = true;
        }
        return b;
      }

      function renderBreadcrumb(partes, alAtras) {
        var bc = document.createElement('div');
        bc.className = 'dfc-wizard-breadcrumb';

        if (alAtras) {
          var backBtn = document.createElement('button');
          backBtn.type = 'button';
          backBtn.className = 'dfc-wizard-back';
          backBtn.textContent = '← ' + cfg.textos.atras;
          backBtn.addEventListener('click', alAtras);
          bc.appendChild(backBtn);
        }

        if (partes.length) {
          var trail = document.createElement('div');
          trail.className = 'dfc-wizard-trail';
          partes.forEach(function (parte, idx) {
            if (idx > 0) {
              var sep = document.createElement('span');
              sep.className = 'dfc-wizard-sep';
              sep.textContent = '/';
              trail.appendChild(sep);
            }
            trail.appendChild(crumbBtn(parte.texto, parte.onClick));
          });
          bc.appendChild(trail);
        }

        wizard.appendChild(bc);
      }

      function opcionCard(imagen, titulo, onClick, variante) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dfc-wizard-card' + (variante ? ' dfc-wizard-card--' + variante : '');

        var media = document.createElement('span');
        media.className = 'dfc-wizard-card__media';
        if (imagen) {
          var img = document.createElement('img');
          img.src = imagen;
          img.alt = titulo;
          img.loading = 'lazy';
          media.appendChild(img);
        }

        var label = document.createElement('span');
        label.className = 'dfc-wizard-card__label';
        label.textContent = titulo;

        btn.appendChild(media);
        btn.appendChild(label);
        btn.addEventListener('click', onClick);
        return btn;
      }

      function productCard(item) {
        var a = document.createElement('a');
        a.href = item.url;
        a.className = 'dfc-card dfc-wizard-product';

        var media = document.createElement('div');
        media.className = 'dfc-card__media';
        if (item.oferta) {
          var badge = document.createElement('span');
          badge.className = 'dfc-card__badge';
          badge.textContent = 'Oferta';
          media.appendChild(badge);
        }
        if (item.image) {
          var img = document.createElement('img');
          img.src = item.image;
          img.alt = item.alt || item.title;
          img.loading = 'lazy';
          media.appendChild(img);
        }

        var body = document.createElement('div');
        body.className = 'dfc-card__body';
        var title = document.createElement('p');
        title.className = 'dfc-card__title';
        title.textContent = item.title;
        var price = document.createElement('p');
        price.className = 'dfc-card__price';
        // priceHtml se genera en el servidor con Liquid (money filter), no
        // viene de ningún dato escrito por el cliente: es seguro insertarlo.
        price.innerHTML = item.priceHtml || '';

        body.appendChild(title);
        body.appendChild(price);
        a.appendChild(media);
        a.appendChild(body);
        return a;
      }

      function renderPaso1() {
        limpiar();
        var texto = document.createElement('p');
        texto.className = 'dfc-wizard-intro';
        texto.textContent = cfg.textos.paso1;
        wizard.appendChild(texto);

        var grid = document.createElement('div');
        grid.className = 'dfc-wizard-grid dfc-wizard-grid--3 dfc-wizard-grid--genero';
        ['hombre', 'mujer', 'nino'].forEach(function (g) {
          grid.appendChild(opcionCard(cfg.imagenes[g], cfg.labels[g], function () {
            renderPaso2(g);
          }, 'genero'));
        });
        wizard.appendChild(grid);
      }

      function renderPaso2(genero) {
        limpiar();
        renderBreadcrumb(
          [{ texto: cfg.labels[genero], onClick: null }],
          function () { renderPaso1(); }
        );

        var texto = document.createElement('p');
        texto.className = 'dfc-wizard-intro';
        texto.textContent = cfg.textos.paso2;
        wizard.appendChild(texto);

        var tipos = tiposDisponibles(genero);
        var grid = document.createElement('div');
        grid.className = 'dfc-wizard-grid dfc-wizard-grid--' + Math.max(1, Math.min(tipos.length, 4));
        tipos.forEach(function (t) {
          grid.appendChild(opcionCard(cfg.imagenes[t], cfg.labels[t], function () {
            avanzarAMaterial(genero, t);
          }));
        });
        wizard.appendChild(grid);
      }

      function avanzarAMaterial(genero, tipo) {
        var materiales = materialesDisponibles(genero, tipo);
        if (materiales.length > 1) {
          renderPaso3(genero, tipo, materiales);
        } else {
          // Solo hay un material disponible para esta combinación: no
          // tiene sentido obligar a elegir, así que se aplica solo y se
          // pasa directo al resultado (queda reflejado en la miga de pan).
          renderPaso4(genero, tipo, materiales[0], true);
        }
      }

      function renderPaso3(genero, tipo, materiales) {
        limpiar();
        renderBreadcrumb(
          [
            { texto: cfg.labels[genero], onClick: function () { renderPaso1(); } },
            { texto: cfg.labels[tipo], onClick: null }
          ],
          function () { renderPaso2(genero); }
        );

        var texto = document.createElement('p');
        texto.className = 'dfc-wizard-intro';
        texto.textContent = cfg.textos.paso3;
        wizard.appendChild(texto);

        var grid = document.createElement('div');
        grid.className = 'dfc-wizard-grid dfc-wizard-grid--2';
        var poolTipo = itemsPorGenero(genero).filter(function (i) { return i.tipo === tipo; });
        materiales.forEach(function (m) {
          // La foto de esta tarjeta tiene que ser de la categoría que se
          // está mirando (p. ej. una camiseta si se está eligiendo entre
          // camisetas), no una foto genérica fija de "técnico"/"normal"
          // que antes siempre enseñaba una sudadera aunque se estuviera
          // mirando otra prenda.
          var ejemplo = poolTipo.filter(function (i) { return i.material === m; })[0];
          var imagen = (ejemplo && ejemplo.image) ? ejemplo.image : cfg.imagenes[m];
          grid.appendChild(opcionCard(imagen, cfg.labels[m], function () {
            renderPaso4(genero, tipo, m, false);
          }));
        });
        wizard.appendChild(grid);
      }

      function renderPaso4(genero, tipo, material, materialSaltado) {
        limpiar();
        var materialTexto = cfg.labels[material] + (materialSaltado ? ' (' + cfg.textos.unicaOpcion + ')' : '');

        renderBreadcrumb(
          [
            { texto: cfg.labels[genero], onClick: function () { renderPaso1(); } },
            { texto: cfg.labels[tipo], onClick: function () { renderPaso2(genero); } },
            {
              texto: materialTexto,
              onClick: materialSaltado ? null : function () {
                renderPaso3(genero, tipo, materialesDisponibles(genero, tipo));
              }
            }
          ],
          function () {
            if (materialSaltado) {
              renderPaso2(genero);
            } else {
              renderPaso3(genero, tipo, materialesDisponibles(genero, tipo));
            }
          }
        );

        var resultados = itemsFinal(genero, tipo, material);
        var grid = document.createElement('div');
        grid.className = 'dfc-catalogo__grid dfc-wizard-resultados';
        if (resultados.length) {
          resultados.forEach(function (item) {
            grid.appendChild(productCard(item));
          });
        } else {
          var vacio = document.createElement('p');
          vacio.className = 'dfc-cuerpo';
          vacio.style.opacity = '0.7';
          vacio.textContent = cfg.textos.sinResultados;
          grid.appendChild(vacio);
        }
        wizard.appendChild(grid);

        var reiniciar = document.createElement('button');
        reiniciar.type = 'button';
        reiniciar.className = 'dfc-btn dfc-btn--outline dfc-wizard-reiniciar';
        reiniciar.textContent = cfg.textos.empezarDeNuevo;
        reiniciar.addEventListener('click', renderPaso1);
        wizard.appendChild(reiniciar);
      }

      renderPaso1();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initPrevisualizador();
    initCatalogoWizard();
  });
})();
